<template>
  <v-dialog max-width="500px" v-model="dialog" @keydown.enter.prevent="onenter">
    <v-card>
      <v-card-title>
        <span class="headline">Currency kind ({{ this.rules.name }})</span>
        <v-spacer/>
        <r-authorize
            :master="masterAccess"
            :locked.sync="model.locked"
            :enabled.sync="model.enabled"/>
      </v-card-title>

      <v-card-text>
        <v-col cols="4" sm="2" md="2">
          <v-btn @click="showPreview()" class="mr-2" small>View JSON</v-btn>
        </v-col>
        <v-row dense>
          <v-col cols="12" sm="4" md="4">
            <v-text-field
                label="Key:"
                v-model.trim="model.key"
                v-ascii-only
            />
          </v-col>

          <v-col cols="12" sm="4" md="4">
            <v-text-field label="Code:" v-model.number="model.code"/>
          </v-col>

          <v-col cols="12" md="4" sm="4">
            <r-item-kind-dropdown :model.sync="model.item"/>
          </v-col>

          <v-col cols="12" md="4" sm="4">
            <r-currency-group-dropdown :model.sync="model.group"/>
          </v-col>

          <v-col cols="12" md="4" sm="4">
            <r-currency-kind-dropdown
                :model.sync="model.modifier"
                :modifier="true"
                label="Modifier"
            />
          </v-col>

          <v-col cols="12" md="4" sm="4">
            <v-text-field label="Weight:" v-model.number="model.weight"/>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" sm="3" md="3">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                    v-model.number="model.amount"
                    v-on="on"
                    v-bind="attrs"
                    label="Amount"
                    hide-details
                />
              </template>
              <ul>
                <li>Default currency amount</li>
              </ul>
            </v-tooltip>
          </v-col>
          <v-col cols="12" sm="3" md="3">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                    v-model.number="model.capacity"
                    v-on="on"
                    v-bind="attrs"
                    label="Capacity"
                    hide-details
                />
              </template>
              <ul>
                <li>Maximum capacity per account</li>
                <li>disabled = 0</li>
              </ul>
            </v-tooltip>
          </v-col>
          <v-col cols="12" sm="3" md="3">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                    v-model.number="model.recoveryCapacity"
                    v-on="on"
                    v-bind="attrs"
                    label="Recovery Capacity"
                    hide-details
                />
              </template>
              <ul>
                <li>Maximum capacity per recoverable duration</li>
                <li>disabled = 0</li>
              </ul>
            </v-tooltip>
          </v-col>
          <v-col cols="12" sm="3" md="3">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                    v-model.number="model.recoveryDuration"
                    v-on="on"
                    v-bind="attrs"
                    label="Recovery Duration"
                    hide-details
                />
              </template>
              <ul>
                <li>Currency recovery duration</li>
                <li>disabled = 0</li>
              </ul>
            </v-tooltip>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="4" sm="4">
            <v-checkbox
                v-model="model.shared"
                label="Shared"
            ></v-checkbox>
          </v-col>
          <v-col cols="12" md="4" sm="4">
            <v-checkbox
                v-model="model.collectEffect"
                label="Collect Effect"
            ></v-checkbox>
          </v-col>
        </v-row>
        <v-row dense class="mt-4">
          <v-expansion-panels focusable>
            <r-owners-panel :model="model"/>
            <r-maps-panel :model="model"/>
          </v-expansion-panels>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer/>
        <v-btn @click="cancel" color="blue darken-1" text>
          Cancel
        </v-btn>
        <v-btn @click="save" color="blue darken-1" text v-if="!lockedAssess || masterAccess">
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
    <r-search-dialog ref="r-search-dialog"/>
    <r-preview-dialog ref="preview-dialog"></r-preview-dialog>
  </v-dialog>
</template>

<script>
import VueTypes from "vue-types";
import {ViewRules} from "@/common/ViewRules";
import {RedactorUtils} from "@/modules/redactor/core/RedactorUtils";
import {CurrencyKindModel} from "@/modules/redactor/models/currency/CurrencyKindModel";

import RSearchDialog from "@/modules/redactor/views/search/r-search-dialog.vue";
import RAuthorize from "@/modules/redactor/views/common/r-authorize";
import RCurrencyGroupDropdown from "@/modules/redactor/views/currency-group/r-currency-group-dropdown.vue";
import RCurrencyKindDropdown from "@/modules/redactor/views/currency-kind/r-currency-kind-dropdown.vue";
import RItemKindDropdown from "@/modules/redactor/views/item-kind/r-item-kind-dropdown.vue";
import ROwnersPanel from "@/modules/redactor/views/utils/r-owners-panel.vue";
import RMapsPanel from "@/modules/redactor/views/utils/r-maps-panel.vue";
import RPreviewDialog from "@/modules/redactor/views/preview/r-preview-dialog.vue";


export default {
  name: "r-currency-kind-dialog",
  components: {
    RPreviewDialog,
    RSearchDialog,
    RAuthorize,
    RCurrencyGroupDropdown,
    RCurrencyKindDropdown,
    RItemKindDropdown,
    ROwnersPanel,
    RMapsPanel
  },
  data: () => ({
    dialog: false,
    resolve: null,
    reject: null,
    model: VueTypes.objectOf(CurrencyKindModel),
    rules: ViewRules.READONLY,
  }),
  computed: {
    lockedAssess() {
      return this.model.locked;
    },
    masterAccess() {
      return RedactorUtils.hasMasterAccess();
    }
  },
  methods: {
    show(rules, model) {
      this.dialog = true;
      this.rules = rules;
      this.model = model || new CurrencyKindModel();

      if (!this.model.item) this.model.item = RedactorUtils.getItemKindByKey("none");

      if (ViewRules.COPY.equal(this.rules)) {
        this.model.locked = false;
      }

      if (!Array.isArray(this.model.owners)) {
        this.model.owners = [];
      }

      if (!Array.isArray(this.model.maps)) {
        this.model.maps = [];
      }

      if (ViewRules.CREATE.equal(this.rules) || ViewRules.COPY.equal(this.rules)) {
        const allItems = RedactorUtils.getCurrencyKinds();
        const maxCode = allItems.reduce((max, item) =>
            Number.isInteger(item.code) && item.code > max ? item.code : max, -1);
        this.model.code = maxCode + 1;
      }

      return new Promise((resolve, reject) => {
        this.resolve = resolve;
        this.reject = reject;
      });
    },
    save() {
      const allItems = RedactorUtils.getCurrencyKinds();

      const duplicateKey = allItems.find(item =>
          item.key === this.model.key && item.uid !== this.model.uid
      );

      if (duplicateKey) {
        this.$alert("Duplicate key detected!");
        return;
      }

      const duplicateCode = allItems.find(item =>
          item.code === this.model.code && item.uid !== this.model.uid
      );

      if (duplicateCode) {
        this.$alert("Duplicate code detected!");
        return;
      }

      if (this.model.valid()) {
        this.resolve(this.model);
        this.discard();
      } else {
        this.$alert("Invalid config!");
      }
    },
    cancel() {
      this.reject();
      this.discard();
    },
    discard() {
      this.dialog = false;
    },
    onenter() {
      if (!this.lockedAssess || this.masterAccess) {
        this.save();
      } else {
        this.cancel();
      }
    },
    showPreview() {
      this.$refs['preview-dialog'].show(this.rules, this.model, CurrencyKindModel)
          .then((result) => {
            Object.assign(this.model, result);
          })
          .catch((e) => {
            this.$alert("Failed to parse config!" + e.message);
          });
    }
  }
};
</script>

<style lang="scss" scoped>
.fill-width {
  width: 100%;
}
</style>
